// Microphone capture for streaming dictation.
//
// Primary path: an AudioWorklet (loaded from /pcm-tap.worklet.js — a
// real same-origin file, because CSP script-src 'self' refuses blob:
// modules) taps raw Float32 blocks off the mic stream, immune to
// background-tab rAF throttling. If the worklet fails to load for any
// reason, a ScriptProcessorNode fallback keeps the mic working —
// deprecated API, but universally supported; a broken mic is worse.
//
// The main thread resamples to PCM16 mono 24 kHz and packs 100 ms
// frames for the uplink. Browser audio processing (echo cancellation /
// noise suppression / auto gain) is left ON: the old code disabled it
// and quiet mics never crossed the speech threshold, which silently
// discarded whole batches.

import { Resampler } from '@shared/resample.js';
import { LevelSmoother } from '@shared/level.js';

export interface CaptureHandle {
  stream: MediaStream;
  /** 'worklet' | 'script-processor' — which capture path is running. */
  path: 'worklet' | 'script-processor';
  stop(): Promise<void>;
}

export interface CaptureOpts {
  /** Called with each 100 ms PCM16 mono 24 kHz frame (4800 bytes). */
  onFrame: (frame: ArrayBuffer) => void;
  /** Called ~20×/s with a smoothed, perceptual level percentage (0–100).
   * See shared/level.ts for the scale anchors. */
  onLevel: (levelPct: number) => void;
}

const TARGET_RATE = 24000;
const FRAME_SAMPLES = 2400; // 100 ms at 24 kHz
const WORKLET_URL = '/pcm-tap.worklet.js';

export async function startCapture(opts: CaptureOpts): Promise<CaptureHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) {
    for (const t of stream.getTracks()) t.stop();
    throw new Error('Web Audio API not available in this browser');
  }
  const ctx = new Ctor();
  if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);

  const source = ctx.createMediaStreamSource(stream);
  const resampler = new Resampler(ctx.sampleRate, TARGET_RATE);
  const level = new LevelSmoother(ctx.sampleRate);
  let pcmBuf = new Int16Array(0);

  const handleBlock = (block: Float32Array, rms: number): void => {
    // Raw per-block RMS arrives ~375×/s and swings wildly inside a
    // single syllable; the smoother aggregates to ~50 ms windows with
    // VU-style attack/release so the meter reads calmly.
    const pct = level.push(rms, block.length);
    if (pct !== null) opts.onLevel(pct);
    const chunk = resampler.process(block);
    const merged = new Int16Array(pcmBuf.length + chunk.length);
    merged.set(pcmBuf);
    merged.set(chunk, pcmBuf.length);
    pcmBuf = merged;
    while (pcmBuf.length >= FRAME_SAMPLES) {
      const frame = pcmBuf.slice(0, FRAME_SAMPLES);
      pcmBuf = pcmBuf.slice(FRAME_SAMPLES);
      opts.onFrame(frame.buffer);
    }
  };

  let path: CaptureHandle['path'];
  let teardownNode: () => void;

  try {
    await ctx.audioWorklet.addModule(WORKLET_URL);
    const tap = new AudioWorkletNode(ctx, 'pcm-tap');
    tap.port.onmessage = (ev: MessageEvent) => {
      const { block, rms } = ev.data as { block: Float32Array; rms: number };
      handleBlock(block, rms);
    };
    source.connect(tap);
    // Never connect the tap onward — no monitoring loop, no echo.
    path = 'worklet';
    teardownNode = () => {
      tap.port.onmessage = null;
      try { source.disconnect(); tap.disconnect(); } catch { /* torn down */ }
    };
  } catch (err) {
    // Worklet refused (CSP, file missing, old browser) — fall back.
    // eslint-disable-next-line no-console
    console.warn('[mic] AudioWorklet unavailable, using ScriptProcessor fallback:', err);
    const proc = ctx.createScriptProcessor(4096, 1, 1);
    proc.onaudioprocess = (ev: AudioProcessingEvent) => {
      const block = ev.inputBuffer.getChannelData(0);
      let sum = 0;
      for (let i = 0; i < block.length; i += 1) sum += block[i]! * block[i]!;
      handleBlock(new Float32Array(block), Math.sqrt(sum / block.length));
    };
    source.connect(proc);
    // ScriptProcessor only fires while connected to a destination; a
    // zero-gain node keeps it silent.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    proc.connect(mute);
    mute.connect(ctx.destination);
    path = 'script-processor';
    teardownNode = () => {
      proc.onaudioprocess = null;
      try { source.disconnect(); proc.disconnect(); mute.disconnect(); } catch { /* torn down */ }
    };
  }

  return {
    stream,
    path,
    async stop() {
      teardownNode();
      await ctx.close().catch(() => undefined);
      for (const t of stream.getTracks()) t.stop();
    },
  };
}
